import { useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { inviteGuestToChannel } from '../lib/api';
import { isAdmin, type ChannelWithMembership, type PublicUser } from '@avslack/shared';
import { useChannelMembers, useChannelMemberActions } from '../data/channels';
import { useUsers } from '../data/users';
import { Dialog, useAnimatedClose } from './Dialog';
import { Avatar } from './Avatar';
import { UserLink } from './UserLink';
import { useConfirm } from './Confirm';
import { PresenceDot } from './PresenceDot';

/** Ekezet-fuggetlen egyezes: a "eva" a "Kis Éva"-t is megtalalja. */
const fold = (value: string): string =>
  value
    .toLowerCase()
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '');

/**
 * A csatorna tagsaga.
 *
 * Ez valtja ki az eredetileg tervezett "csapat" reteget: a jogosultsag
 * felhasznalo-szinten, a csatorna tagsagan mulik, es ez a parbeszed teszi
 * lathatova es kezelhetove. Egy masodik, parhuzamos jogosultsagi fogalom
 * (csapat) csak ket forrasbol adodo lathatosagot jelentett volna - es a
 * jogosultsagi hibak pont ott a legdragabbak.
 */
export function ChannelMembersDialog({
  open,
  onOpenChange,
  channel,
  me,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  channel: ChannelWithMembership;
  me: PublicUser;
}) {
  const closeAnimated = useAnimatedClose(() => onOpenChange(false));
  const { data: memberIds, isPending } = useChannelMembers(open ? channel.id : null);
  const { users, nameOf, presence } = useUsers();
  const { add, remove } = useChannelMemberActions(channel.id);
  const confirm = useConfirm();

  const [query, setQuery] = useState('');
  const [adding, setAdding] = useState(false);
  const [guestEmail, setGuestEmail] = useState('');
  const [fullHistory, setFullHistory] = useState(false);
  const [guestError, setGuestError] = useState<string | null>(null);
  const queryClient = useQueryClient();

  const inviteGuest = useMutation({
    mutationFn: () => inviteGuestToChannel(channel.id, guestEmail.trim(), fullHistory),
    onSuccess: () => {
      setGuestEmail('');
      setFullHistory(false);
      setGuestError(null);
      void queryClient.invalidateQueries({ queryKey: ['channel-members', channel.id] });
    },
    onError: (error: unknown) => {
      setGuestError(error instanceof Error ? error.message : 'A behívás nem sikerült.');
    },
  });

  /**
   * Ki kezelheti a tagsagot?
   *
   * Ugyanaz a szabaly, mint a szerveren (`requireManageAccess`): a csatorna
   * letrehozoja vagy workspace-admin. A felulet csak ELREJTI, amit a szerver
   * amugy is elutasitana - a dontes ott szuletik, nem itt.
   */
  const canManage = isAdmin(me) || channel.createdBy === me.id;

  const members = useMemo(() => {
    const ids = new Set(memberIds ?? []);
    return users
      .filter((user) => ids.has(user.id))
      .sort((a, b) => nameOf(a).localeCompare(nameOf(b), 'hu'));
  }, [memberIds, users, nameOf]);

  const candidates = useMemo(() => {
    const ids = new Set(memberIds ?? []);
    const needle = fold(query.trim());
    if (!needle) return [];

    return (
      users
        .filter((user) => !ids.has(user.id) && user.isActive && !user.isBot)
        // Masik cegbol csak vendegkent jon be valaki (lent, admin) - a szerver is elutasitja.
        .filter((user) => channel.teamId === null || user.teamId === channel.teamId)
        .filter(
          (user) =>
            fold(user.displayName).includes(needle) ||
            fold(user.nickname ?? '').includes(needle) ||
            fold(user.email).includes(needle),
        )
        .slice(0, 8)
    );
  }, [memberIds, users, query, channel.teamId]);

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title="A csatorna tagjai"
      description={`${members.length} fő${canManage ? '' : ' · a tagságot a csatorna létrehozója vagy egy admin kezeli'}`}
      width="lg"
    >
      {canManage && (
        <div className="mt-4">
          <input
            aria-label="Tag keresése"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="Kit vegyünk fel? Név vagy e-mail…"
            className="w-full rounded-control border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-accent"
          />

          {query.trim() && candidates.length === 0 && (
            <p className="mt-2 text-xs text-ink-faint">
              Nincs találat — vagy már mindenki bent van, aki illik a keresésre.
            </p>
          )}

          {candidates.length > 0 && (
            <ul className="mt-2 space-y-0.5 rounded-control border border-border-soft p-1">
              {candidates.map((user) => (
                <li key={user.id}>
                  <button
                    type="button"
                    disabled={adding}
                    onClick={() => {
                      setAdding(true);
                      add.mutate([user.id], {
                        onSettled: () => {
                          setAdding(false);
                          setQuery('');
                        },
                      });
                    }}
                    className="flex w-full items-center gap-2 rounded-control px-2 py-1.5 text-left transition-colors hover:bg-surface-hover disabled:opacity-50"
                  >
                    <Avatar
                      userId={user.id}
                      name={user.displayName}
                      avatarUrl={user.avatarUrl}
                      size="sm"
                    />
                    <span className="min-w-0 flex-1">
                      <span className="block truncate text-sm">{nameOf(user)}</span>
                      <span className="block truncate text-2xs text-ink-faint">{user.email}</span>
                    </span>
                    <span className="shrink-0 text-xs text-accent">Hozzáadás</span>
                  </button>
                </li>
              ))}
            </ul>
          )}

          {/*
            VENDEG MASIK CEGBOL - pontos e-mail cimmel.

            A fenti kereso csak azokat kinalja, akiket UGYIS latunk: a sajat
            csapatunkat es a kozos csatornak tagjait. Egy masik ceg embere ott
            nem jelenik meg - es ez nem hiany, hanem a lenyeg: a nevsorok nem
            latszanak at egymason.

            Aki behiv valakit, az tudja a cimet. Ez a mezo ezert nem keres,
            hanem CIMET var.
          */}
          {isAdmin(me) && (
            <div className="mt-4 rounded-control border border-border-soft bg-surface-sunken/50 p-3">
              <p className="text-xs font-semibold">Vendég másik cégből</p>
              <p className="mt-0.5 text-2xs text-ink-faint">
                Add meg a pontos e-mail címét. A cégek névsora egymás elől rejtve van, ezért itt
                nincs keresés.
              </p>

              <div className="mt-2 flex flex-wrap gap-2">
                <input
                  type="email"
                  autoComplete="off"
                  spellCheck={false}
                  aria-label="Vendég e-mail-címe"
                  value={guestEmail}
                  onChange={(event) => {
                    setGuestEmail(event.target.value);
                    setGuestError(null);
                  }}
                  placeholder="nev@masikceg.hu"
                  className="min-w-0 flex-1 rounded-control border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-accent"
                />
                <button
                  type="button"
                  disabled={!guestEmail.trim() || inviteGuest.isPending}
                  onClick={() => inviteGuest.mutate()}
                  className="shrink-0 rounded-control bg-accent px-3 py-2 text-sm font-semibold text-accent-ink disabled:opacity-40"
                >
                  Behívás
                </button>
              </div>

              {/*
                Az ELOZMENY alapbol NEM latszik neki.

                Egy masik cegbol jott ember kulonben visszaolvashatna honapokat
                olyan belso beszelgetesbol, amit nem neki szantak - csendben,
                mert ez nem hibaval jelentkezik, hanem azzal, hogy valaki
                elolvas valamit. Ha megis kell, az legyen tudatos dontes.
              */}
              <label className="mt-2 flex items-start gap-2 text-2xs text-ink-muted">
                <input
                  type="checkbox"
                  checked={fullHistory}
                  onChange={(event) => setFullHistory(event.target.checked)}
                  className="mt-0.5"
                />
                <span>
                  Láthatja a csatorna teljes történetét
                  <span className="block text-ink-faint">
                    Enélkül csak a behívás utáni üzeneteket látja.
                  </span>
                </span>
              </label>

              {guestError && (
                <p role="alert" className="mt-2 text-xs text-danger">
                  {guestError}
                </p>
              )}
            </div>
          )}
        </div>
      )}

      <div className="mt-4 max-h-[calc(var(--app-height,100dvh)*0.45)] overflow-y-auto">
        {isPending ? (
          <p className="py-6 text-center text-sm text-ink-faint">Töltődik…</p>
        ) : (
          <ul className="space-y-0.5">
            {members.map((user) => (
              <li
                key={user.id}
                className="group flex items-center gap-2.5 rounded-control px-2 py-1.5 transition-colors hover:bg-surface-hover"
              >
                {/* Ugyanaz a mozdulat, mint a folyamban: nev vagy kep -> DM. */}
                <UserLink
                  userId={user.id}
                  meId={me.id}
                  disabled={user.isBot}
                  label={nameOf(user)}
                  onDone={closeAnimated}
                  className="shrink-0 rounded-md"
                >
                  <Avatar
                    userId={user.id}
                    name={user.displayName}
                    avatarUrl={user.avatarUrl}
                    size="sm"
                  />
                </UserLink>

                <span className="min-w-0 flex-1">
                  <span className="flex items-center gap-1.5">
                    <UserLink
                      userId={user.id}
                      meId={me.id}
                      disabled={user.isBot}
                      label={nameOf(user)}
                      onDone={closeAnimated}
                      className="truncate text-sm hover:underline"
                    >
                      {nameOf(user)}
                    </UserLink>
                    {user.id === me.id && <span className="text-2xs text-ink-faint">(te)</span>}
                    {user.isBot && (
                      <span className="rounded bg-surface-sunken px-1 text-2xs text-ink-faint">
                        bot
                      </span>
                    )}
                    {user.id === channel.createdBy && (
                      <span className="rounded bg-surface-sunken px-1 text-2xs text-ink-faint">
                        létrehozó
                      </span>
                    )}
                  </span>
                  <span className="block truncate text-2xs text-ink-faint">{user.title ?? ''}</span>
                </span>

                <PresenceDot presence={presence[user.id] ?? 'offline'} />

                {/*
                  Sajat magunkat nem itt vesszuk ki, hanem a fejlec "Kilepes"
                  gombjaval - az a sajat dontes, ez masé.
                */}
                {canManage && user.id !== me.id && (
                  <button
                    type="button"
                    disabled={remove.isPending}
                    onClick={() => {
                      void confirm({
                        title: 'Kivétel a csatornából',
                        body: (
                          <>
                            <strong>{nameOf(user)}</strong> nem látja többé ezt a csatornát, és a
                            korábbi üzeneteit sem. Vissza lehet venni, de az előzményt újra látni
                            fogja.
                          </>
                        ),
                        confirmLabel: 'Kivétel',
                      }).then((ok) => {
                        if (ok) remove.mutate(user.id);
                      });
                    }}
                    className="shrink-0 rounded-control px-2 py-0.5 text-2xs text-ink-faint opacity-0 transition-opacity hover:text-danger group-hover:opacity-100 focus-visible:opacity-100 disabled:opacity-50 [@media(hover:none)]:opacity-100"
                  >
                    Kivétel
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}
      </div>

      {(add.isError || remove.isError) && (
        <p
          role="alert"
          className="mt-3 rounded-control bg-[var(--danger-soft)] px-3 py-2 text-xs text-ink"
        >
          {(add.error ?? remove.error)?.message ?? 'A művelet nem sikerült.'}
        </p>
      )}

      {/* A megjelenitett nev nelkuli felhasznalok is legyenek azonosithatok. */}
      {!isPending && members.length !== (memberIds?.length ?? 0) && (
        <p className="mt-3 text-2xs text-ink-faint">
          {(memberIds?.length ?? 0) - members.length} tagot nem tudunk megjeleníteni (törölt vagy
          még be nem töltött fiók).
        </p>
      )}
    </Dialog>
  );
}
